import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { AlertTriangle, Users, Loader2, ArrowRight, Clock } from 'lucide-react';

export const CapacityUpgradeModal = ({ workspaceId }) => {
  const { api } = useAuth();
  const [alert, setAlert] = useState(null);
  const [open, setOpen] = useState(false);
  const [upgrading, setUpgrading] = useState(null);
  const [acking, setAcking] = useState(false);

  const fetchAlert = useCallback(async () => {
    try {
      const res = await api.get(`/api/workspaces/${workspaceId}/onboarding/capacity-alert`);
      if (res.data?.pending?.length > 0) {
        setAlert(res.data);
        setOpen(true);
      }
    } catch { /* silent */ }
  }, [api, workspaceId]);

  useEffect(() => {
    fetchAlert();
    const t = setInterval(fetchAlert, 120000);
    return () => clearInterval(t);
  }, [fetchAlert]);

  const remindLater = async () => {
    setAcking(true);
    try {
      await api.post(`/api/workspaces/${workspaceId}/onboarding/capacity-alert/ack`);
    } catch { /* silent */ }
    setAcking(false);
    setOpen(false);
  };

  const upgrade = async (plan) => {
    setUpgrading(plan.id);
    try {
      await api.post(`/api/workspaces/${workspaceId}/change-plan`, { plan_id: plan.id, archive_sub_accounts: [] });
      const res = await api.post(`/api/workspaces/${workspaceId}/onboarding/capacity-alert/resolve`);
      const n = res.data?.created?.length || 0;
      if (n > 0) toast.success(`🎉 Upgraded to ${plan.name} — ${n} client account${n > 1 ? 's' : ''} created automatically!`);
      else toast.success(`Upgraded to ${plan.name}`);
      if (res.data?.remaining_no_capacity > 0) {
        toast.warning(`${res.data.remaining_no_capacity} submission(s) still need more slots — check the Submissions tab.`);
      }
      setOpen(false);
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upgrade failed');
    } finally {
      setUpgrading(null);
    }
  };

  if (!alert || !open) return null;
  const { pending, current_plan, upgrade_plans } = alert;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[70] p-4"
        data-testid="capacity-upgrade-modal"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          className="w-full max-w-lg bg-space-800 rounded-2xl overflow-hidden border-2 border-amber-400/40"
          style={{ boxShadow: '0 0 60px -15px rgba(251,191,36,0.45)' }}
        >
          <div className="p-6 border-b border-white/10 relative overflow-hidden">
            <div className="absolute -top-14 -right-14 w-44 h-44 rounded-full blur-3xl opacity-25 pointer-events-none"
                 style={{ background: 'radial-gradient(circle, #fbbf24 0%, transparent 70%)' }} />
            <div className="flex items-center gap-3 relative">
              <div className="w-11 h-11 rounded-xl bg-amber-400/20 flex items-center justify-center shrink-0 animate-pulse">
                <AlertTriangle className="text-amber-300" size={22} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">You're out of sub-account slots!</h3>
                <p className="text-sm text-gray-400">
                  {pending.length === 1
                    ? `${pending[0].business_name || 'A new client'} completed onboarding, but auto-creation was blocked.`
                    : `${pending.length} clients completed onboarding, but auto-creation was blocked.`}
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-5">
            <div className="rounded-xl border border-white/10 bg-white/[0.03] divide-y divide-white/5">
              {pending.slice(0, 3).map(p => (
                <div key={p.submission_id} className="px-4 py-2.5 flex items-center gap-3" data-testid={`capacity-pending-${p.submission_id}`}>
                  <Clock size={13} className="text-amber-300 shrink-0" />
                  <span className="text-sm text-white truncate">{p.business_name || 'New Client'}</span>
                  <span className="text-xs text-gray-500 truncate ml-auto">{p.contact_name}</span>
                </div>
              ))}
              {pending.length > 3 && (
                <p className="px-4 py-2 text-xs text-gray-500">+{pending.length - 3} more waiting…</p>
              )}
            </div>

            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Users size={14} className="text-teal-400" />
              Using <span className="text-white font-semibold">{current_plan?.active_count}/{current_plan?.sub_account_limit}</span>
              sub-accounts on your <span className="text-white font-semibold capitalize">{current_plan?.id}</span> plan
            </div>

            {upgrade_plans.length > 0 ? (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Upgrade now — their accounts get created instantly</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {upgrade_plans.slice(0, 4).map(plan => (
                    <button
                      key={plan.id}
                      onClick={() => upgrade(plan)}
                      disabled={!!upgrading}
                      className="p-4 rounded-xl border border-amber-400/30 bg-gradient-to-b from-amber-400/[0.08] to-transparent text-left hover:border-amber-400/60 hover:shadow-[0_0_25px_-8px_rgba(251,191,36,0.5)] transition-all disabled:opacity-50 group"
                      data-testid={`capacity-plan-${plan.id}`}
                    >
                      <p className="text-sm font-semibold text-white flex items-center justify-between">
                        {plan.name}
                        {upgrading === plan.id
                          ? <Loader2 size={14} className="animate-spin text-amber-300" />
                          : <ArrowRight size={14} className="text-amber-300 opacity-0 group-hover:opacity-100 transition-opacity" />}
                      </p>
                      <p className="text-lg font-bold text-amber-200 mt-0.5">${plan.monthly}<span className="text-[11px] text-gray-500 font-normal">/mo</span></p>
                      <p className="text-xs text-gray-400 mt-0.5">{plan.subAccounts === 999 ? 'Unlimited' : plan.subAccounts} sub-accounts</p>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-400">You're already on the highest plan — contact support to raise your limit.</p>
            )}
          </div>

          <div className="px-6 pb-6 flex items-center justify-between">
            <button
              onClick={remindLater}
              disabled={acking}
              className="text-sm text-gray-500 hover:text-gray-300 transition-colors disabled:opacity-50"
              data-testid="capacity-remind-later"
            >
              {acking ? 'Dismissing…' : 'Remind me later'}
            </button>
            <p className="text-[11px] text-gray-600">Their submissions stay saved either way</p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CapacityUpgradeModal;
